import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/client';
import { ServiceInput } from '../types';

export function useServices(params?: Record<string, string>) {
  return useQuery({
    queryKey: ['services', params],
    queryFn: () => api.getServices(params),
    refetchInterval: 30000,
  });
}

export function useService(id: number) {
  return useQuery({
    queryKey: ['service', id],
    queryFn: () => api.getService(id),
    enabled: id > 0,
  });
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ServiceInput) => api.createService(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['services'] }); },
  });
}

export function useUpdateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ServiceInput }) =>
      api.updateService(id, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['service', vars.id] });
    },
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteService(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['services'] }); },
  });
}

export function useTriggerCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.triggerCheck(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['service', id] });
      qc.invalidateQueries({ queryKey: ['checks', id] });
    },
  });
}

export function useChecks(id: number, limit = 100, hours?: number) {
  return useQuery({
    queryKey: ['checks', id, limit, hours ?? null],
    queryFn: () => api.getChecks(id, limit, 0, hours),
    enabled: id > 0,
  });
}

export function useTestServiceConfig() {
  return useMutation({ mutationFn: api.testServiceConfig });
}

export function useServiceTypes() {
  return useQuery({
    queryKey: ['service-types'],
    queryFn: api.getServiceTypes,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRotatePushToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.rotatePushToken(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['service', id] });
    },
  });
}

export function useHourlyStats(id: number, hours = 168) {
  return useQuery({
    queryKey: ['hourly', id, hours],
    queryFn: () => api.getHourlyStats(id, hours),
    enabled: id > 0,
  });
}

export function useServiceTemplates() {
  return useQuery({
    queryKey: ['service-templates'],
    queryFn: api.getServiceTemplates,
    staleTime: 60 * 1000,
  });
}

export function useDeleteServiceTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteServiceTemplate(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service-templates'] }); },
  });
}
